import PageShell from "@/components/PageShell";
import useSEO, { orgSchema, breadcrumbSchema } from "@/lib/useSEO";
import { Link } from "react-router-dom";
import { ArrowRight, Rocket, Users, Target } from "lucide-react";

export default function About() {
  useSEO({
    title: "About PixelEmpire AI — The Modern Million Dollar Homepage",
    description:
      "PixelEmpire AI is a permanent advertising grid inspired by Alex Tew's Million Dollar Homepage. 10,000 blocks. One-time pricing. Lifetime placement. Do-follow SEO backlinks. Built for the next decade of independent web culture.",
    path: "/about",
    jsonLd: [
      orgSchema,
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "About", url: "/about" },
      ]),
    ],
  });

  return (
    <PageShell testId="page-about">
      <div className="pill" data-testid="about-pill"><span>About</span></div>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl leading-tight text-white" data-testid="about-h1">
        Building the internet's most <span className="text-gradient">permanent</span> ad grid
      </h1>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl leading-relaxed">
        In 2005 a British student named <a href="https://en.wikipedia.org/wiki/The_Million_Dollar_Homepage" className="text-cyan-300 underline">Alex Tew</a> sold a million pixels of advertising space
        for $1 each. Within four months every pixel had sold. Twenty years later most of those
        buyers still have their ad live — a $1 spend that has now been visible to millions of visitors.
      </p>
      <p className="mt-4 text-base sm:text-lg text-[var(--fg-muted)] max-w-3xl leading-relaxed">
        PixelEmpire AI carries that idea into 2026 with better tech, better UX, and — most importantly —
        real SEO value baked in. Every block includes a permanent do-follow backlink from our public
        directory, so buying a block isn't just buying a logo slot; it's buying a lifetime piece of
        search-engine authority.
      </p>

      <section className="mt-12 grid sm:grid-cols-3 gap-4">
        {[
          { icon: Rocket, title: "10,000 blocks. Forever.", body: "The grid is capped at exactly 100×100 = 10,000 blocks. No expansion, no new inventory, no dilution." },
          { icon: Target, title: "Real SEO payoff", body: "Every block appears on our do-follow /directory page with a keyword-rich 50-word description you control." },
          { icon: Users, title: "Built for founders", body: "Bootstrapped founders, indie creators, and micro-brands are our people. No enterprise gatekeeping." },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="glass p-5 rounded-xl">
            <Icon className="w-5 h-5 text-neon-cyan" />
            <h2 className="mt-3 font-display text-lg text-white">{title}</h2>
            <p className="mt-2 text-sm text-[var(--fg-muted)] leading-relaxed">{body}</p>
          </div>
        ))}
      </section>

      <section className="mt-14">
        <h2 className="font-display text-2xl text-white">Our commitments</h2>
        <ul className="mt-4 space-y-3 text-sm sm:text-base text-[var(--fg-muted)] list-disc list-inside">
          <li>We will never change a customer's ad content without permission.</li>
          <li>Prices only ever go UP. Anyone who buys today locks in the lowest rate ever.</li>
          <li>The site's uptime target is 99.9% and we publish real analytics per block.</li>
          <li>If we ever wind down the platform, block owners get at least 12 months notice.</li>
          <li>No advertiser tracking beyond the analytics we show them. No re-selling data.</li>
        </ul>
      </section>

      <div className="mt-14 flex flex-wrap gap-3">
        <Link to="/" className="btn-primary" data-testid="about-cta-primary">
          Claim your block <ArrowRight className="w-4 h-4" />
        </Link>
        <Link to="/how-it-works" className="btn-ghost">How it works</Link>
      </div>
    </PageShell>
  );
}
